import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Helmet } from 'react-helmet-async';
import { 
  faCalendarAlt, 
  faUser, 
  faTag, 
  faArrowLeft, 
  faClock,
  faShare,
  faComment,
  faHeart
} from '@fortawesome/free-solid-svg-icons';
import {
  faFacebook, 
  faTwitter, 
  faLinkedin 
} from '@fortawesome/free-brands-svg-icons';

// Bloglar için örnek veri - gerçek projede API'den alınabilir
// BlogPage.js ile aynı veriyi kullandığımızdan emin olun
const blogPosts = [
  {
    id: 'secure-access-service-edge',
    title: 'What is Secure Access Service Edge (SASE)?',
    excerpt: "In today's rapidly evolving digital landscape, businesses are more connected than ever, but with this comes increased risks in terms of network security. Traditional security measures are now inadequate for safeguarding distributed organizations.",
    author: 'Doğukan Yaylacı',
    date: 'September 24, 2024',
    category: 'Security',
    image: 'https://via.placeholder.com/1200x600?text=SASE',
    tags: ['Security', 'Networking', 'Cloud'],
    readTime: '8 min read',
    content: `
      <p>In today's rapidly evolving digital landscape, businesses are more connected than ever, but with this comes increased risks in terms of network security. Traditional security measures are now inadequate for safeguarding distributed organizations, particularly as the use of cloud services and remote workforces continues to rise. This is where <strong>Secure Access Service Edge (SASE)</strong> comes into play, transforming the way companies secure their data and networks.</p>
      
      <h2>Understanding Secure Access Service Edge (SASE)</h2>
      
      <p><strong>Secure Access Service Edge</strong>, or <strong>SASE</strong>, is a relatively new network architecture that converges <strong>networking</strong> and <strong>security services</strong> into a unified, cloud-native service model. It was introduced by Gartner in 2019 to address the challenges of modern enterprise networks, which are increasingly dispersed and reliant on cloud-based applications. The core concept of SASE is to combine <strong>wide area network (WAN)</strong> capabilities with comprehensive security functions to ensure seamless, secure access to data and applications from anywhere, at any time.</p>
      
      <p>SASE merges several critical technologies, including:</p>
      
      <ul>
        <li><strong>Software-defined wide area network (SD-WAN)</strong></li>
        <li><strong>Zero Trust Network Access (ZTNA)</strong></li>
        <li><strong>Firewall as a Service (FWaaS)</strong></li>
        <li><strong>Secure Web Gateways (SWG)</strong></li>
        <li><strong>Cloud Access Security Brokers (CASB)</strong></li>
      </ul>
      
      <p>These elements work together to provide <strong>end-to-end security</strong> and optimized network performance, making SASE an ideal solution for enterprises with distributed teams and increasing reliance on cloud services.</p>
      
      <h2>Key Components of SASE Architecture</h2>
      
      <p>The effectiveness of SASE lies in its architecture, which integrates several technologies into a cohesive framework. Let's dive deeper into the <strong>key components</strong> that form a SASE solution:</p>
      
      <h3><strong>1. Software-defined Wide Area Network (SD-WAN)</strong></h3>
      
      <p><strong>SD-WAN</strong> is the backbone of SASE, providing a more flexible, efficient way to manage and route traffic across wide area networks. Unlike traditional WAN, which relies on physical infrastructure and dedicated connections, SD-WAN leverages <strong>software</strong> to intelligently route traffic based on performance needs, reducing latency and improving the overall user experience. SD-WAN in a SASE model dynamically optimizes connections between <strong>data centers</strong>, <strong>cloud services</strong>, and <strong>remote users</strong>.</p>
      
      <h3><strong>2. Zero Trust Network Access (ZTNA)</strong></h3>
      
      <p>The <strong>Zero Trust</strong> model is built on the principle of "<strong>never trust, always verify</strong>." ZTNA assumes that threats can come from both inside and outside the network, and therefore, every user and device must be authenticated and verified before gaining access to any resource. SASE integrates ZTNA to ensure that network access is tightly controlled, reducing the attack surface and minimizing the risk of unauthorized access.</p>
      
      <h3><strong>3. Firewall as a Service (FWaaS)</strong></h3>
      
      <p>A traditional firewall is no longer enough in a cloud-centric world. <strong>FWaaS</strong> delivers firewall protection from the cloud, enabling consistent security policies across all network traffic, regardless of location. This scalable approach is critical for enterprises with remote employees or multiple branch locations. FWaaS ensures <strong>traffic inspection</strong>, <strong>malware detection</strong>, and <strong>intrusion prevention</strong> are applied to all data entering and exiting the network.</p>
      
      <h3><strong>4. Secure Web Gateway (SWG)</strong></h3>
      
      <p><strong>SWG</strong> plays a vital role in protecting users from accessing malicious websites and downloading harmful content. By monitoring and filtering web traffic, SWG helps to prevent <strong>data breaches</strong> and ensure compliance with corporate security policies. Within the SASE framework, SWG ensures that all web interactions are secure, particularly when users access cloud applications.</p>
      
      <h3><strong>5. Cloud Access Security Broker (CASB)</strong></h3>
      
      <p>A <strong>CASB</strong> sits between users and cloud services, providing visibility and control over data moving between them. CASB solutions monitor cloud activity, enforce security policies, and provide threat protection. With <strong>SASE</strong>, CASB ensures that security controls extend to cloud-based applications, mitigating the risks associated with shadow IT and <strong>unapproved SaaS</strong> usage.</p>
      
      <h2>Why SASE is Critical for Modern Enterprises</h2>
      
      <h3><strong>1. Scalability and Flexibility</strong></h3>
      
      <p>The traditional network security approach involves deploying separate hardware solutions at each site, which can be complex and costly to manage. SASE eliminates the need for this by delivering security services through the cloud, making it highly scalable and flexible. Organizations can <strong>add new users</strong>, <strong>expand to new locations</strong>, or <strong>adopt new cloud services</strong> without compromising on security or network performance.</p>
      
      <h3><strong>2. Improved Security Posture</strong></h3>
      
      <p>SASE enhances an organization's overall <strong>security posture</strong> by adopting a holistic approach to security. Instead of relying on individual point solutions, SASE integrates all security measures into a unified platform, ensuring that all network traffic is secured. This reduces <strong>security gaps</strong>, improves visibility, and streamlines the application of security policies across the network.</p>
      
      <h3><strong>3. Cost Efficiency</strong></h3>
      
      <p>By consolidating several security and networking services into a single, cloud-delivered solution, SASE reduces the need for expensive on-premises hardware and the associated maintenance costs. This approach also reduces the complexity of managing multiple security vendors, resulting in lower <strong>operational costs</strong> and more predictable pricing.</p>
      
      <h3><strong>4. Enhanced User Experience</strong></h3>
      
      <p>SASE optimizes <strong>network performance</strong> by dynamically routing traffic based on real-time conditions, ensuring that users experience <strong>minimal latency</strong> and <strong>fast access</strong> to cloud applications. This is particularly important for remote workers or employees in branch offices, where network performance can vary significantly. With SASE, traffic is automatically routed through the most efficient path, delivering a <strong>consistent user experience</strong> regardless of location.</p>
      
      <h3><strong>5. Zero Trust Approach</strong></h3>
      
      <p>The <strong>Zero Trust</strong> model incorporated in SASE ensures that <strong>security controls</strong> are applied consistently, regardless of where users or devices are located. By requiring <strong>strict authentication</strong> and <strong>access controls</strong>, SASE reduces the risk of internal and external threats, ensuring that sensitive data and applications are only accessible to authorized individuals.</p>
      
      <h2>SASE Use Cases</h2>
      
      <p>Organizations across various industries can benefit from SASE, especially those that are moving towards a more <strong>cloud-first</strong> strategy. Here are some of the primary use cases:</p>
      
      <h3><strong>1. Remote Workforces</strong></h3>
      
      <p>The rise of remote workforces has significantly expanded the attack surface for many businesses. SASE allows remote employees to securely access company resources, cloud applications, and data without the need for traditional <strong>VPNs</strong>. With <strong>ZTNA</strong>, remote users are authenticated, and their access to the network is controlled based on their identity, device, and security posture.</p>
      
      <h3><strong>2. Cloud Adoption</strong></h3>
      
      <p>For businesses that are shifting their workloads to <strong>public and private clouds</strong>, SASE ensures that <strong>data in transit</strong> is secure and that access to cloud services is governed by strict security policies. CASB and SWG functionalities are particularly important in these scenarios, providing visibility into cloud usage and preventing data leakage.</p>
      
      <h3><strong>3. Mergers and Acquisitions</strong></h3>
      
      <p>During a merger or acquisition, integrating two different IT environments can be a complex task. SASE simplifies this process by providing a unified <strong>security architecture</strong> that can be deployed across both organizations quickly. This ensures that security and networking are aligned from day one, reducing the risk of <strong>data breaches</strong> and <strong>downtime</strong>.</p>
      
      <h2>Challenges and Considerations for SASE Adoption</h2>
      
      <p>While the benefits of SASE are significant, there are several challenges that organizations may face when adopting this new architecture:</p>
      
      <ul>
        <li><strong>Migration Complexity</strong>: Transitioning from a traditional network security model to a cloud-based SASE solution can be complex, requiring careful planning and execution.</li>
        <li><strong>Vendor Selection</strong>: As SASE is a relatively new concept, organizations need to choose the right vendors who can deliver a comprehensive solution that fits their needs.</li>
        <li><strong>Cost of Implementation</strong>: Although SASE can reduce long-term costs, there may be significant upfront investments required to implement the solution.</li>
      </ul>
      
      <h2>Conclusion</h2>
      
      <p>As businesses continue to adopt cloud services and remote working becomes the norm, the need for a more scalable, flexible, and secure network architecture is critical. <strong>SASE</strong> provides an innovative approach to addressing the challenges of modern enterprise networks by converging <strong>networking</strong> and <strong>security</strong> into a single, cloud-delivered service. By adopting SASE, organizations can achieve <strong>enhanced security</strong>, <strong>improved performance</strong>, and <strong>lower operational costs</strong>, making it an essential part of the future of network security.</p>
      
      <h3>FAQs About Secure Access Service Edge (SASE)</h3>
      
      <h4>What is Secure Access Service Edge (SASE)?</h4>
      
      <p>SASE is a cloud-native network architecture that combines networking and security services into a unified solution. It integrates technologies like SD-WAN, Zero Trust Network Access (ZTNA), Firewall as a Service (FWaaS), and more to provide secure, optimized access to cloud services and remote users.</p>
      
      <h4>How does SASE improve network security?</h4>
      
      <p>SASE enhances security by applying a Zero Trust model, which requires strict authentication and verification for all users and devices. It also integrates cloud-based security functions like Secure Web Gateway (SWG) and Cloud Access Security Broker (CASB) to protect against threats and monitor cloud activity.</p>
      
      <h4>What are the benefits of using SASE for remote workforces?</h4>
      
      <p>SASE provides secure, reliable access to corporate resources from any location, eliminating the need for traditional VPNs. By using ZTNA and SD-WAN, it optimizes network performance while ensuring strong security controls for remote employees.</p>
      
      <h4>Can SASE reduce IT costs?</h4>
      
      <p>Yes, SASE can lower IT costs by consolidating multiple networking and security solutions into a single platform. It eliminates the need for expensive on-premises hardware and reduces the complexity of managing multiple vendors, resulting in cost savings over time.</p>
      
      <h4>What challenges should businesses consider before adopting SASE?</h4>
      
      <p>Organizations may face challenges like migration complexity, vendor selection, and initial costs of implementation. Careful planning and choosing the right vendor are critical for a successful SASE deployment.</p>
    `,
    related: ['web-security-best-practices', 'cloud-native-architecture']
  },
  {
    id: 'ai-in-content-creation',
    title: 'The Role of AI in Modern Content Creation',
    excerpt: "Artificial Intelligence is revolutionizing how we create and consume content. This article explores the latest AI tools for content creators and how they're reshaping the digital landscape.",
    author: 'Doğukan Yaylacı',
    date: 'November 10, 2024',
    category: 'Technology',
    image: 'https://via.placeholder.com/1200x600?text=AI+Content+Creation',
    tags: ['AI', 'Content', 'Technology'],
    readTime: '6 min read',
    content: `
      <p>When it comes to content creation in 2025, AI tools have become indispensable for professionals across industries. From automated writing assistants to sophisticated image generators, these technologies are revolutionizing how we produce and consume digital content.</p>
      
      <h2>How AI is Changing Content Creation</h2>
      
      <p>Artificial Intelligence has made significant strides in understanding and generating human language. Today's AI tools can:</p>
      
      <ul>
        <li>Generate complete articles based on simple prompts</li>
        <li>Create compelling images and graphics from text descriptions</li>
        <li>Optimize content for search engines with remarkable precision</li>
        <li>Translate content across dozens of languages while maintaining context</li>
        <li>Transform written content into natural-sounding audio</li>
      </ul>
      
      <h2>The Impact on Content Creators</h2>
      
      <p>For professional creators, AI has become both a powerful ally and a disruptive force. Many are finding that these tools allow them to focus on strategy and creativity while automating more mundane aspects of content production.</p>
      
      <blockquote>
        "The most successful content creators today aren't those fighting against AI, but those who have mastered the art of collaborating with it." - Digital Content Association
      </blockquote>
      
      <p>As we look to the future, the symbiotic relationship between human creativity and AI assistance will likely define the next era of digital content across all platforms.</p>
    `,
    related: ['responsible-ai-development', 'future-of-frontend-development']
  },
  {
    id: 'web-security-best-practices',
    title: 'Web Security Best Practices for 2025',
    excerpt: 'Cybersecurity threats continue to evolve. Learn the essential web security practices that every developer and IT professional should implement to protect their digital assets in 2025.',
    author: 'Doğukan Yaylacı',
    date: 'October 5, 2024',
    category: 'Security',
    image: 'https://via.placeholder.com/1200x600?text=Web+Security',
    tags: ['Security', 'Web Development', 'Cybersecurity'],
    readTime: '8 min read',
    content: `
      <p>As web technologies continue to advance, so do the methods used by malicious actors to exploit vulnerabilities. In 2025, staying ahead of security threats requires a proactive approach and awareness of the latest cybersecurity best practices.</p>
      
      <h2>Critical Security Measures for Modern Web Applications</h2>
      
      <p>The security landscape has evolved significantly over the past few years. Here are the most important security practices that every web developer and IT professional should implement:</p>
      
      <ul>
        <li><strong>Zero Trust Architecture</strong> - Never trust, always verify. Implement strict identity verification for every person and device attempting to access resources.</li>
        <li><strong>API Security</strong> - As applications become more API-driven, securing these endpoints has become essential. Implement rate limiting, proper authentication, and input validation.</li>
        <li><strong>Runtime Application Self-Protection (RASP)</strong> - Embed security tools that can detect and prevent attacks in real-time while the application is running.</li>
        <li><strong>Web3 Security Considerations</strong> - For decentralized applications, implement smart contract audits and blockchain-specific security measures.</li>
        <li><strong>Automated Security Testing</strong> - Integrate security testing into your CI/CD pipeline to catch vulnerabilities before they reach production.</li>
      </ul>
      
      <h2>Addressing Emerging Threats</h2>
      
      <p>Several new threats have emerged that require specific attention:</p>
      
      <ol>
        <li><strong>AI-powered Attacks</strong> - As attackers leverage AI to create sophisticated phishing campaigns and find vulnerabilities, defenders need AI-enhanced security tools.</li>
        <li><strong>Supply Chain Vulnerabilities</strong> - Secure your dependencies and third-party integrations as thoroughly as your own code.</li>
        <li><strong>Advanced XSS and CSRF Attacks</strong> - Modern frameworks provide some protection, but custom defenses are still necessary for complete security.</li>
      </ol>
      
      <blockquote>
        "The most secure systems are those built with security in mind from the beginning, not as an afterthought." - National Cybersecurity Alliance
      </blockquote>
      
      <p>By implementing these practices and staying informed about emerging threats, developers can significantly reduce their vulnerability to cyber attacks in today's increasingly complex digital landscape.</p>
    `,
    related: ['secure-access-service-edge', 'cloud-native-architecture']
  },
  {
    id: 'future-of-frontend-development',
    title: 'The Future of Frontend Development',
    excerpt: 'From WebAssembly to AI-assisted coding, discover the emerging technologies and approaches that will define frontend development in the coming years.',
    author: 'Doğukan Yaylacı',
    date: 'September 12, 2024',
    category: 'Development',
    image: 'https://via.placeholder.com/1200x600?text=Frontend+Development',
    tags: ['Frontend', 'Development', 'Web Technologies'],
    readTime: '7 min read',
    content: `
      <p>Frontend development continues to evolve at a breathtaking pace. As we look ahead to the next few years, several emerging technologies and methodologies stand poised to reshape how we build user interfaces and web experiences.</p>
      
      <h2>WebAssembly: Beyond JavaScript</h2>
      
      <p>WebAssembly (Wasm) has matured from a promising technology to an essential part of the web platform. In 2025, we're seeing:</p>
      
      <ul>
        <li>Complex desktop-like applications running at near-native speed in browsers</li>
        <li>More languages leveraging Wasm to target the web, including Rust, C++, and Go</li>
        <li>Component model standardization allowing for better code sharing and smaller downloads</li>
        <li>Web-based gaming experiences that rival native performance</li>
      </ul>
      
      <h2>AI-Assisted Development</h2>
      
      <p>Artificial intelligence has dramatically changed how frontend code is written:</p>
      
      <ul>
        <li>AI pair programmers that can generate entire components from descriptions</li>
        <li>Automated refactoring and optimization of existing codebases</li>
        <li>UI generation from simple prompts or sketches</li>
        <li>Intelligent debugging assistance that can predict and identify bugs before users encounter them</li>
      </ul>
      
      <h2>Design Systems and Component-Driven Development</h2>
      
      <p>The way teams organize and build frontend code has also evolved significantly:</p>
      
      <blockquote>
        "The most successful frontend teams in 2025 are those who've mastered the art of building with small, composable pieces that scale across their entire digital ecosystem." - Web Components Conference
      </blockquote>
      
      <p>Modern frontend projects increasingly rely on:</p>
      
      <ul>
        <li>Design tokens as the single source of truth for visual styling</li>
        <li>Atomic design principles applied at scale</li>
        <li>Standardized component APIs that work across frameworks</li>
        <li>Live documentation that evolves alongside the codebase</li>
      </ul>
      
      <p>As we continue through 2025, these trends will likely accelerate, making frontend development both more powerful and more accessible to developers at all skill levels.</p>
    `,
    related: ['ai-in-content-creation', 'web-security-best-practices']
  },
  {
    id: 'cloud-native-architecture',
    title: 'Understanding Cloud-Native Architecture',
    excerpt: 'Dive into the principles and benefits of cloud-native architecture and how it enables organizations to build scalable, resilient applications designed specifically for cloud environments.',
    author: 'Doğukan Yaylacı',
    date: 'August 15, 2024',
    category: 'Cloud',
    image: 'https://via.placeholder.com/1200x600?text=Cloud+Native',
    tags: ['Cloud', 'Architecture', 'Microservices'],
    readTime: '8 min read',
    content: `
      <p>Cloud-native architecture represents a fundamental shift in how applications are designed, built, and deployed. By embracing the cloud's distributed nature, organizations can create solutions that are inherently scalable, resilient, and agile.</p>
      
      <h2>Core Principles of Cloud-Native Architecture</h2>
      
      <p>Several key principles define the cloud-native approach:</p>
      
      <ul>
        <li><strong>Microservices</strong> - Breaking applications into small, loosely coupled services that can be developed, deployed, and scaled independently.</li>
        <li><strong>Containers</strong> - Packaging applications and dependencies into lightweight, portable containers for consistent operation across environments.</li>
        <li><strong>Dynamic Orchestration</strong> - Using tools like Kubernetes to automate deployment, scaling, and management of containerized applications.</li>
        <li><strong>API-Driven Design</strong> - Building services with well-defined interfaces to enable modular architecture and service discovery.</li>
        <li><strong>Infrastructure as Code</strong> - Defining infrastructure through code to enable automation and consistency.</li>
      </ul>
      
      <h2>Benefits of Embracing Cloud-Native</h2>
      
      <p>Organizations that adopt cloud-native architectural patterns can realize significant advantages:</p>
      
      <ol>
        <li><strong>Scalability</strong> - Services can scale independently based on demand.</li>
        <li><strong>Resilience</strong> - Distributed systems can be designed to withstand failures gracefully.</li>
        <li><strong>Agility</strong> - Smaller, independent services enable faster innovation and continuous delivery.</li>
        <li><strong>Observability</strong> - Comprehensive monitoring, logging, and tracing capabilities.</li>
        <li><strong>Cost Optimization</strong> - Resources can be adjusted dynamically to match workload requirements.</li>
      </ol>
      
      <blockquote>
        "Cloud-native isn't just about where your applications run—it's about how they're built, deployed, and managed." - Cloud Native Computing Foundation
      </blockquote>
      
      <p>By embracing cloud-native principles, organizations are positioned to take full advantage of what modern cloud platforms offer while avoiding the limitations of traditional monolithic architectures.</p>
    `,
    related: ['secure-access-service-edge', 'web-security-best-practices']
  },
  {
    id: 'responsible-ai-development',
    title: 'Ethical Considerations in AI Development',
    excerpt: 'As AI becomes more prevalent in our daily lives, developers must consider the ethical implications of their work. This article explores responsible AI development practices and guidelines.',
    author: 'Doğukan Yaylacı',
    date: 'July 22, 2024',
    category: 'Technology',
    image: 'https://via.placeholder.com/1200x600?text=Ethical+AI',
    tags: ['AI', 'Ethics', 'Technology'],
    readTime: '7 min read',
    content: `
      <p>Artificial Intelligence continues to transform industries and daily life, but with its growing influence comes significant ethical responsibilities. Developers and organizations must consider how AI systems impact individuals and society.</p>
      
      <h2>Key Ethical Considerations</h2>
      
      <p>As AI systems become more powerful and autonomous, several ethical dimensions require careful attention:</p>
      
      <ul>
        <li><strong>Fairness and Bias</strong> - Ensuring AI systems don't perpetuate or amplify existing biases based on race, gender, age, or other protected attributes.</li>
        <li><strong>Transparency and Explainability</strong> - Creating systems where decisions can be understood and explained, particularly in high-stakes contexts.</li>
        <li><strong>Privacy and Data Governance</strong> - Respecting user data rights while balancing innovation needs.</li>
        <li><strong>Accountability</strong> - Establishing clear lines of responsibility for AI outcomes and impacts.</li>
        <li><strong>Safety and Robustness</strong> - Building systems that operate reliably and safely, even under unexpected conditions.</li>
      </ul>
      
      <h2>Implementing Ethical AI Practices</h2>
      
      <p>Moving from principles to practice requires concrete steps throughout the AI development lifecycle:</p>
      
      <ol>
        <li><strong>Diverse Development Teams</strong> - Include perspectives from varied backgrounds to identify potential blind spots.</li>
        <li><strong>Ethics by Design</strong> - Integrate ethical considerations from the earliest planning stages, not as an afterthought.</li>
        <li><strong>Regular Bias Audits</strong> - Test systems with diverse datasets to identify and address potential bias.</li>
        <li><strong>Stakeholder Engagement</strong> - Involve those who will be affected by AI systems in the development process.</li>
        <li><strong>Ongoing Monitoring</strong> - Continue assessing AI systems after deployment to catch emerging issues.</li>
      </ol>
      
      <blockquote>
        "The most advanced AI will be those that recognize the necessity of ethical frameworks and human values in their operation." - International AI Ethics Consortium
      </blockquote>
      
      <p>By embracing responsible AI development practices, technologists can help ensure that AI advances benefit humanity while minimizing potential harms and unintended consequences.</p>
    `,
    related: ['ai-in-content-creation', 'future-of-frontend-development']
  }
];

// Yorum verisi
const sampleComments = [
  {
    id: 1,
    name: 'Alex Johnson',
    date: 'March 5, 2025',
    text: 'This is an excellent article! Very comprehensive and useful information.',
    likes: 12,
    replies: [
      {
        id: 4,
        name: 'Doğukan Yaylacı',
        date: 'March 5, 2025',
        text: "Thank you for your feedback, Alex! I'm glad you found it helpful.",
        likes: 3
      }
    ]
  },
  {
    id: 2,
    name: 'Maria Garcia',
    date: 'March 4, 2025',
    text: 'I have been looking for this information for a while. Thanks for putting it all together in such a clear way.',
    likes: 8,
    replies: []
  },
  {
    id: 3,
    name: 'John Smith',
    date: 'March 3, 2025',
    text: 'Great insights! Would love to see a follow-up article on related topics.',
    likes: 5,
    replies: []
  }
];

const BlogDetailContainer = styled.div`
  padding: 120px 50px 80px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 120px 20px 60px;
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: #666;
  text-decoration: none;
  margin-bottom: 30px;
  transition: color 0.3s;
  
  svg {
    margin-right: 10px;
  }
  
  &:hover {
    color: #000;
  }
`;

const BlogHeader = styled.div`
  margin-bottom: 40px;
`;

const BlogTitle = styled(motion.h1)`
  font-size: 2.5rem;
  margin-bottom: 20px;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const BlogMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 30px;
  color: #666;
  font-size: 0.9rem;
  
  div {
    display: flex;
    align-items: center;
    
    svg {
      margin-right: 8px;
    }
  }
`;

const BlogImage = styled.div`
  width: 100%;
  height: 500px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 40px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const BlogContent = styled.div`
  line-height: 1.8;
  color: #333;
  font-size: 1.1rem;
  
  h2 {
    font-size: 1.8rem;
    margin: 40px 0 20px;
  }
  
  p {
    margin-bottom: 20px;
  }
  
  ul, ol {
    margin-bottom: 20px;
    padding-left: 20px;
    
    li {
      margin-bottom: 10px;
    }
  }
  
  blockquote {
    border-left: 4px solid #000;
    padding-left: 20px;
    font-style: italic;
    margin: 30px 0;
    color: #555;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    
    h2 {
      font-size: 1.5rem;
    }
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 40px 0;
`;

const Tag = styled(Link)`
  padding: 6px 15px;
  background-color: #f0f0f0;
  color: #666;
  text-decoration: none;
  border-radius: 20px;
  font-size: 0.9rem;
  transition: all 0.3s;
  
  &:hover {
    background-color: #000;
    color: #fff;
  }
`;

const ShareSection = styled.div`
  margin: 40px 0;
  padding: 20px 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
`;

const ShareTitle = styled.p`
  font-weight: 600;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 10px;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 15px;
`;

const SocialIcon = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: ${props => props.bgcolor || '#f0f0f0'};
  color: ${props => props.color || '#333'};
  border-radius: 50%;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-3px);
    opacity: 0.9;
  }
`;

const RelatedPosts = styled.div`
  margin: 60px 0;
`;

const RelatedTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 30px;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 40px;
    height: 2px;
    background-color: #000;
  }
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RelatedCard = styled(Link)`
  display: flex;
  text-decoration: none;
  color: inherit;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0,0,0,0.1);
    
    h4 {
      color: #555;
    }
  }
`;

const RelatedImage = styled.div`
  width: 120px;
  height: 120px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RelatedContent = styled.div`
  padding: 15px;
  flex-grow: 1;
`;

const RelatedPostTitle = styled.h4`
  font-size: 1rem;
  margin-bottom: 10px;
  transition: color 0.3s;
`;

const RelatedDate = styled.p`
  font-size: 0.8rem;
  color: #666;
`;

const CommentsSection = styled.div`
  margin: 60px 0;
`;

const CommentsTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 15px;
  }
`;

const CommentForm = styled.form`
  margin-bottom: 40px;
`;

const FormTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #999;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 150px;
  
  &:focus {
    outline: none;
    border-color: #999;
  }
`;

const SubmitButton = styled(motion.button)`
  padding: 12px 24px;
  background-color: #000;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  
  &:hover {
    background-color: #333;
  }
`;

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const CommentCard = styled.div`
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const CommentAuthor = styled.h5`
  font-size: 1rem;
  font-weight: 600;
`;

const CommentDate = styled.p`
  font-size: 0.8rem;
  color: #666;
`;

const CommentBody = styled.p`
  line-height: 1.6;
  margin-bottom: 15px;
`;

const CommentActions = styled.div`
  display: flex;
  gap: 20px;
`;

const CommentAction = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: #666;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.3s;
  padding: 0;
  
  svg {
    margin-right: 5px;
  }
  
  &:hover {
    color: #000;
  }
`;

const ReplyContainer = styled.div`
  margin-top: 20px;
  margin-left: 30px;
  padding-left: 20px;
  border-left: 2px solid #ddd;
`;

const BlogDetailPage = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [comments, setComments] = useState([]);
  
  useEffect(() => {
    // Gerçek projede API isteği yapılabilir
    // Burada örnek olarak veriyi kullanıyoruz
    setTimeout(() => {
      const foundPost = blogPosts.find(post => post.id === id);
      setPost(foundPost);
      
      if (foundPost && foundPost.related) {
        const related = foundPost.related.map(relatedId => 
          blogPosts.find(post => post.id === relatedId)
        ).filter(Boolean);
        setRelatedPosts(related);
      }
      
      setComments(sampleComments);
      setLoading(false);
      
      // Sayfa yüklendiğinde en üste scroll yap
      window.scrollTo(0, 0);
    }, 300);
  }, [id]);
  
  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading Blog Post | Dogukan Yaylaci</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <BlogDetailContainer>
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <h2>Loading...</h2>
          </div>
        </BlogDetailContainer>
      </>
    );
  }
  
  if (!post) {
    return (
      <>
        <Helmet>
          <title>Blog Post Not Found | Dogukan Yaylaci</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <BlogDetailContainer>
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <h2>Blog post not found</h2>
            <p>The blog post you're looking for doesn't exist or has been removed.</p>
            <BackButton to="/blog">
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Blog
            </BackButton>
          </div>
        </BlogDetailContainer>
      </>
    );
  }

  // Strip HTML tags for meta description
  const getMetaDescription = (htmlContent) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    return text.substring(0, 160) + '...';
  };

  return (
    <>
      <Helmet>
        <title>{post.title} | Dogukan Yaylaci Blog</title>
        <meta name="description" content={getMetaDescription(post.content)} />
        <meta name="keywords" content={post.tags.join(', ')} />
        <meta name="author" content={post.author} />
        <link rel="canonical" href={`https://dogukanyaylaci.dev/blog/${post.id}`} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.image} />
        <meta property="og:url" content={`https://dogukanyaylaci.dev/blog/${post.id}`} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        {post.tags.map((tag, index) => (
          <meta property="article:tag" content={tag} key={index} />
        ))}
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={post.image} />
      </Helmet>
      
      <BlogDetailContainer>
        <BackButton to="/blog">
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Blog
        </BackButton>
        
        <BlogHeader>
          <BlogTitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {post.title}
          </BlogTitle>
          
          <BlogMeta>
            <div>
              <FontAwesomeIcon icon={faUser} />
              <span>{post.author}</span>
            </div>
            <div>
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>{post.date}</span>
            </div>
            <div>
              <FontAwesomeIcon icon={faTag} />
              <span>{post.category}</span>
            </div>
            <div>
              <FontAwesomeIcon icon={faClock} />
              <span>{post.readTime}</span>
            </div>
          </BlogMeta>
        </BlogHeader>
        
        <BlogImage>
          <img src={post.image} alt={post.title} />
        </BlogImage>
        
        <BlogContent dangerouslySetInnerHTML={{ __html: post.content }} />
        
        <TagsContainer>
          {post.tags.map((tag, index) => (
            <Tag to={`/blog?tag=${tag}`} key={index}>{tag}</Tag>
          ))}
        </TagsContainer>
        
        <ShareSection>
          <ShareTitle>
            <FontAwesomeIcon icon={faShare} /> Share this post
          </ShareTitle>
          <SocialIcons>
            <SocialIcon 
              href={`https://facebook.com/sharer/sharer.php?u=${window.location.href}`}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              bgcolor="#3b5998"
              color="#fff"
            >
              <FontAwesomeIcon icon={faFacebook} />
            </SocialIcon>
            <SocialIcon 
              href={`https://twitter.com/intent/tweet?url=${window.location.href}&text=${post.title}`}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              bgcolor="#1da1f2"
              color="#fff"
            >
              <FontAwesomeIcon icon={faTwitter} />
            </SocialIcon>
            <SocialIcon 
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              bgcolor="#0077b5"
              color="#fff"
            >
              <FontAwesomeIcon icon={faLinkedin} />
            </SocialIcon>
          </SocialIcons>
        </ShareSection>
        
        {relatedPosts.length > 0 && (
          <RelatedPosts>
            <RelatedTitle>Related Posts</RelatedTitle>
            <RelatedGrid>
              {relatedPosts.map(related => (
                <RelatedCard to={`/blog/${related.id}`} key={related.id}>
                  <RelatedImage>
                    <img src={related.image} alt={related.title} />
                  </RelatedImage>
                  <RelatedContent>
                    <RelatedPostTitle>{related.title}</RelatedPostTitle>
                    <RelatedDate>{related.date}</RelatedDate>
                  </RelatedContent>
                </RelatedCard>
              ))}
            </RelatedGrid>
          </RelatedPosts>
        )}
        
        <CommentsSection>
          <CommentsTitle>
            <FontAwesomeIcon icon={faComment} /> Comments ({comments.length})
          </CommentsTitle>
          
          <CommentForm>
            <FormTitle>Leave a comment</FormTitle>
            <FormGroup>
              <FormLabel htmlFor="name">Name*</FormLabel>
              <FormInput type="text" id="name" required />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="email">Email* (will not be published)</FormLabel>
              <FormInput type="email" id="email" required />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="comment">Comment*</FormLabel>
              <FormTextarea id="comment" required />
            </FormGroup>
            <SubmitButton 
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Submit Comment
            </SubmitButton>
          </CommentForm>
          
          <CommentList>
            {comments.map(comment => (
              <CommentCard key={comment.id}>
                <CommentHeader>
                  <CommentAuthor>{comment.name}</CommentAuthor>
                  <CommentDate>{comment.date}</CommentDate>
                </CommentHeader>
                <CommentBody>{comment.text}</CommentBody>
                <CommentActions>
                  <CommentAction>
                    <FontAwesomeIcon icon={faHeart} /> Like ({comment.likes})
                  </CommentAction>
                  <CommentAction>
                    <FontAwesomeIcon icon={faComment} /> Reply
                  </CommentAction>
                </CommentActions>
                
                {comment.replies.length > 0 && (
                  <ReplyContainer>
                    {comment.replies.map(reply => (
                      <CommentCard key={reply.id}>
                        <CommentHeader>
                          <CommentAuthor>{reply.name}</CommentAuthor>
                          <CommentDate>{reply.date}</CommentDate>
                        </CommentHeader>
                        <CommentBody>{reply.text}</CommentBody>
                        <CommentActions>
                          <CommentAction>
                            <FontAwesomeIcon icon={faHeart} /> Like ({reply.likes})
                          </CommentAction>
                        </CommentActions>
                      </CommentCard>
                    ))}
                  </ReplyContainer>
                )}
              </CommentCard>
            ))}
          </CommentList>
        </CommentsSection>
      </BlogDetailContainer>
    </>
  );
};

export default BlogDetailPage; 